/**
 * ConfirmDialog — branded confirmation modal for destructive actions.
 *
 * Push #56 (Ean dogfood 2026-06-15) replacement for `window.confirm()`. The
 * browser-native popup reads as a dev tool ("workspace.olytic.com
 * says…"), which breaks the SaaS vibe Olytic is going for. This is a
 * Salesforce-Lightning-style centered modal using the existing `.modal-*`
 * CSS classes — same chrome the SuggestNewModal + RequestModal already use.
 *
 * Mental model: this is the destructive sibling of the existing modals.
 * Same backdrop, same card, same close X, but with a tone="danger" prop
 * that flips the primary button to red so the confirm action reads as
 * destructive rather than constructive.
 *
 * Usage (callsite owns open/close state):
 *
 *   const [pending, setPending] = useState<{title: string} | null>(null);
 *   ...
 *   <ConfirmDialog
 *     open={pending !== null}
 *     title="Delete chat?"
 *     body={`"${pending?.title}" will be removed from your history.`}
 *     confirmLabel="Delete"
 *     tone="danger"
 *     onConfirm={() => { handleDelete(); setPending(null); }}
 *     onCancel={() => setPending(null)}
 *   />
 *
 * Keyboard:
 *   - Esc → cancel
 *   - Enter → confirm (so muscle-memory carries from window.confirm)
 *
 * Future: this lives in components/ as a shared primitive so any
 * destructive flow (workspace deletion, connector revoke, member remove,
 * permission set delete) routes through the same chrome.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';

export interface ConfirmDialogProps {
  /** Controls visibility. When false, the component returns null. */
  open: boolean;
  /** Headline copy shown at the top of the card. Imperative + question
   *  form works best ("Delete chat?", "Disconnect Google Ads?"). */
  title: string;
  /** Supporting paragraph beneath the title — explain what happens and
   *  whether it's recoverable. Keep to one or two sentences. Accepts
   *  ReactNode (Push 4-1ai) so callers can inline rich impact bullets. */
  body: string | ReactNode;
  /** Primary button label. Should be the verb that names the action,
   *  not "OK" — Lightning-style. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** `danger` flips the primary button to the destructive red; `default`
   *  keeps the standard teal primary. Default `default`. */
  tone?: 'default' | 'danger';
  /** Called when the user confirms. Caller is responsible for closing
   *  the dialog (setting `open` to false). */
  onConfirm: () => void;
  /** Called when the user cancels (button click, backdrop click, Esc). */
  onCancel: () => void;
  /**
   * Push 4-1ai (2026-07-10): rich impact summary rendered as an amber-
   * tinted callout above the buttons. Use for high-blast-radius
   * destructives (delete permission set, uninstall connector, etc.) so
   * the admin sees exactly what breaks before they click. Pass a string
   * for a one-line summary or a ReactNode for structured bullets.
   */
  impact?: string | ReactNode;
  /**
   * Push 4-1ai (2026-07-10): require the user to type this exact string
   * before the Confirm button enables. Salesforce-Lightning "type the name
   * of the org to delete it" pattern. Only appropriate for very-high-blast-
   * radius destructives — plain confirms don't need it. Typically pass the
   * record's display name; for user-removal callsites, use the email
   * because emails are harder to typo. Matching is case-sensitive verbatim
   * by design; friction is the point.
   */
  typeToConfirm?: string;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
  impact,
  typeToConfirm,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);
  // Push 4-1ai (2026-07-10): type-to-confirm gate. Confirm button stays
  // disabled until this input matches the `typeToConfirm` prop exactly.
  // Case-sensitive by design — friction is the point.
  const [typeValue, setTypeValue] = useState('');
  const typeMatches = typeToConfirm === undefined || typeValue === typeToConfirm;

  // Auto-focus behavior:
  //   - When typeToConfirm is set, focus the input so the admin can just
  //     start typing (muscle-memory pattern from Vercel/Linear).
  //   - Otherwise focus the confirm button so Enter fires it (existing
  //     window.confirm muscle-memory).
  useEffect(() => {
    if (!open) return;
    if (typeToConfirm !== undefined && typeInputRef.current !== null) {
      typeInputRef.current.focus();
      setTypeValue('');
    } else if (confirmRef.current !== null) {
      confirmRef.current.focus();
    }
  }, [open, typeToConfirm]);

  // Esc → cancel. Bound at window level so the modal works even if the
  // confirm button doesn't have focus (e.g. user tabbed elsewhere).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const isDanger = tone === 'danger';

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={onCancel}
    >
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 id="confirm-dialog-title" className="modal-title">
            {title}
          </h2>
          <button type="button" className="modal-close" onClick={onCancel} aria-label="Cancel">
            ×
          </button>
        </div>
        <div className="modal-body">
          {typeof body === 'string' ? (
            <p
              style={{ fontSize: 14, lineHeight: 1.55, margin: 0, color: 'var(--olytic-obsidian)' }}
            >
              {body}
            </p>
          ) : (
            body
          )}
          {/* Push 4-1ai (2026-07-10): impact callout. Amber-tinted so it
             reads as caution, not error. Rendered above the type-to-confirm
             input + buttons so admins see it before they act. */}
          {impact !== undefined && (
            // Push 4-1am (2026-07-10): swapped inline hexes → palette tokens
            // (--olytic-amber-50/-200/-900). Same rendered color; the tokens
            // now match the Tailwind amber scale so any other high-caution
            // notice surface can pick up the same palette without drift.
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                background: 'var(--olytic-amber-50)',
                border: '1px solid var(--olytic-amber-200)',
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.5,
                color: 'var(--olytic-amber-900)',
              }}
            >
              {typeof impact === 'string' ? impact : impact}
            </div>
          )}
          {typeToConfirm !== undefined && (
            <div style={{ marginTop: 14 }}>
              <label
                htmlFor="confirm-dialog-type"
                style={{
                  display: 'block',
                  fontSize: 13,
                  color: 'var(--fg-muted)',
                  marginBottom: 6,
                }}
              >
                Type{' '}
                <code
                  style={{
                    background: 'var(--olytic-surface)',
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {typeToConfirm}
                </code>{' '}
                to confirm.
              </label>
              <input
                ref={typeInputRef}
                id="confirm-dialog-type"
                type="text"
                className="form-input"
                value={typeValue}
                onChange={(e) => setTypeValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && typeMatches) {
                    e.preventDefault();
                    onConfirm();
                  }
                }}
                autoComplete="off"
                spellCheck={false}
                style={{ width: '100%' }}
                aria-label={`Type ${typeToConfirm} to confirm`}
              />
            </div>
          )}
          <div
            className="modal-actions"
            style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}
          >
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button
              ref={confirmRef}
              type="button"
              className={isDanger ? 'btn btn-danger' : 'btn btn-primary'}
              onClick={onConfirm}
              disabled={!typeMatches}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && typeMatches) {
                  e.preventDefault();
                  onConfirm();
                }
              }}
              style={!typeMatches ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
